import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { registerLocaleData } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import localeIt from '@angular/common/locales/it';
import localeJa from '@angular/common/locales/ja';
import localeNb from '@angular/common/locales/nb';
import localePl from '@angular/common/locales/pl';
import localeZh from '@angular/common/locales/zh';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_MOMENT_DATE_FORMATS, provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withRouterConfig } from '@angular/router';
import { MissingTranslationHandler, provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxFloatUiModule, NgxFloatUiPlacements } from 'ngx-float-ui';
import { firstValueFrom } from 'rxjs';
import { CustomMissingTranslationHandler } from '@helpers';
import { authInterceptor, errorInterceptor, loaderInterceptor, mockInterceptor } from '@interceptors';
import {
  GoogleService,
  IconsService,
  LocalesService,
  LoginRedirectService,
  NavbarService,
  PermissionService,
  RouterService,
  TimezonesService,
} from '@services';
import { mainRoutes, routerOptions } from './routes/app.routes';

registerLocaleData(localeIt, 'it-IT');
registerLocaleData(localeZh, 'zh-TW');
registerLocaleData(localePl, 'pl-PL');
registerLocaleData(localeJa, 'ja-JP');
registerLocaleData(localeNb, 'nb-NO');

const placement = NgxFloatUiPlacements.RIGHT;

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) =>
  new TranslateHttpLoader(http, '/assets/i18n/', '.json');

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(mainRoutes, withRouterConfig(routerOptions)),
    provideAnimations(),
    provideTranslateService({
      loader: {
        deps: [HttpClient],
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
      },
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: CustomMissingTranslationHandler },
    }),
    provideAppInitializer(async () => {
      const loginService = inject(LoginRedirectService);
      const permissionsService = inject(PermissionService);
      const timezoneService = inject(TimezonesService);
      const localeService = inject(LocalesService);
      const navbarService = inject(NavbarService);
      const googleService = inject(GoogleService);
      await Promise.all([loginService.loadLoginPage(), navbarService.url()]);
      const permissionsPromise = firstValueFrom(permissionsService.permissions$());
      const timezonesPromise = firstValueFrom(timezoneService.timezones$());
      const localesPromise = firstValueFrom(localeService.locales$());
      const googlePromise = firstValueFrom(googleService.load());
      return await Promise.all([permissionsPromise, timezonesPromise, localesPromise, googlePromise]);
    }),
    provideAppInitializer(async () => {
      const routerService = inject(RouterService);
      return await routerService.initRouterCheck();
    }),
    provideAppInitializer(async () => {
      const iconService = inject(IconsService);
      return await iconService.load();
    }),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor, loaderInterceptor, mockInterceptor])),
    provideMomentDateAdapter(MAT_MOMENT_DATE_FORMATS, { strict: true, useUtc: false }),
    importProvidersFrom(NgxFloatUiModule.forRoot({ placement })),
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
};
